import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

// --- SUBCOMPONENTE ---
const LinhaMetodoPagamento = ({ metodo, formasPagamento, provider, onStatusChange }) => {
    const [vinculado, setVinculado] = useState(metodo.vinculado);
    const [localId, setLocalId] = useState(metodo.local_id || '');

    const [termoBusca, setTermoBusca] = useState('');
    const [resultadosFiltrados, setResultadosFiltrados] = useState([]);
    const [formaSelecionada, setFormaSelecionada] = useState(null);
    const [salvando, setSalvando] = useState(false);
    const [desfazendo, setDesfazendo] = useState(false);

    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setResultadosFiltrados([]);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const formaNome = formasPagamento.find(f => String(f.FORMAPAGAMENTO) === String(localId))?.DESCRICAO || '';

    const filtrar = (termo) => {
        setTermoBusca(termo);
        setFormaSelecionada(null);
        const lower = termo.toLowerCase();
        const filtrados = termo
            ? formasPagamento.filter(f =>
                String(f.FORMAPAGAMENTO).includes(lower) ||
                f.DESCRICAO.toLowerCase().includes(lower)
              )
            : formasPagamento;
        setResultadosFiltrados(filtrados.slice(0, 10));
    };

    const selecionarForma = (forma) => {
        setFormaSelecionada(forma);
        setTermoBusca(`${forma.FORMAPAGAMENTO} - ${forma.DESCRICAO}`);
        setResultadosFiltrados([]);
    };

    const vincular = async () => {
        if (!formaSelecionada) return;
        setSalvando(true);
        try {
            await axios.post(`/comercial/integracoes/${provider}/vincular-pagamento`, {
                local_id: formaSelecionada.FORMAPAGAMENTO,
                external_id: metodo.id_externo,
            });
            setLocalId(String(formaSelecionada.FORMAPAGAMENTO));
            setVinculado(true);
            setTermoBusca('');
            setFormaSelecionada(null);
            onStatusChange(metodo.id_externo, true, String(formaSelecionada.FORMAPAGAMENTO));
        } catch (error) {
            alert(error.response?.data?.message || 'Erro ao vincular forma de pagamento.');
        } finally {
            setSalvando(false);
        }
    };

    const desfazer = async () => {
        if (!window.confirm('Tem certeza que deseja remover o vínculo desta forma de pagamento?')) return;
        setDesfazendo(true);
        try {
            await axios.delete(`/comercial/integracoes/${provider}/vincular-pagamento/${metodo.id_externo}`);
            setVinculado(false);
            setLocalId('');
            setTermoBusca('');
            setFormaSelecionada(null);
            onStatusChange(metodo.id_externo, false, null);
        } catch (error) {
            alert(error.response?.data?.message || 'Erro ao desfazer vínculo.');
        } finally {
            setDesfazendo(false);
        }
    };

    return (
        <tr>
            <td>
                <span className="fw-bold">{metodo.nome}</span><br />
                <code className="text-muted small">{metodo.id_externo}</code>
            </td>
            <td>
                {vinculado ? (
                    <div className="alert alert-success p-3 mb-0 d-flex justify-content-between align-items-center shadow-sm">
                        <div>
                            <i className="bi bi-check-circle-fill me-2 fs-5 align-middle"></i>
                            <span className="align-middle">
                                {formaNome || localId}
                                <span className="text-muted small ms-2">(Cód: {localId})</span>
                            </span>
                        </div>
                        <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={desfazer}
                            disabled={desfazendo}
                            title="Desfazer vínculo"
                        >
                            {desfazendo
                                ? <span className="spinner-border spinner-border-sm"></span>
                                : <><i className="bi bi-x-circle me-1"></i> Desfazer</>
                            }
                        </button>
                    </div>
                ) : (
                    <div className="position-relative" ref={containerRef}>
                        <div className="input-group">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Cód. ou descrição..."
                                value={termoBusca}
                                onChange={(e) => filtrar(e.target.value)}
                                onFocus={() => filtrar(termoBusca)}
                            />
                            <button
                                className="btn btn-success"
                                onClick={vincular}
                                disabled={salvando || !formaSelecionada}
                            >
                                {salvando
                                    ? <span className="spinner-border spinner-border-sm"></span>
                                    : <><i className="bi bi-link-45deg me-1"></i> Vincular</>
                                }
                            </button>
                        </div>

                        {resultadosFiltrados.length > 0 && (
                            <ul className="list-group position-absolute w-100 mt-2 shadow-lg" style={{ zIndex: 1000, maxHeight: '280px', overflowY: 'auto', border: '2px solid #0078d4' }}>
                                <li className="list-group-item bg-light text-muted fw-bold p-2 text-center" style={{ fontSize: '0.85rem' }}>
                                    Selecione a forma de pagamento:
                                </li>
                                {resultadosFiltrados.map(f => (
                                    <li
                                        key={f.FORMAPAGAMENTO}
                                        className="list-group-item list-group-item-action p-3"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => selecionarForma(f)}
                                    >
                                        <span className="badge bg-secondary me-2">{f.FORMAPAGAMENTO}</span>
                                        <strong>{f.DESCRICAO}</strong>
                                    </li>
                                ))}
                                <li className="list-group-item text-center p-2 bg-light">
                                    <button
                                        className="btn btn-outline-secondary btn-sm w-100"
                                        onClick={() => { setResultadosFiltrados([]); setTermoBusca(''); }}
                                    >
                                        Fechar lista
                                    </button>
                                </li>
                            </ul>
                        )}
                    </div>
                )}
            </td>
        </tr>
    );
};

// --- COMPONENTE PRINCIPAL ---
const VincularFormasPagamento = ({ tenant, provider, providerLabel }) => {
    const label = providerLabel || provider;
    const [metodos, setMetodos] = useState([]);
    const [formasPagamento, setFormasPagamento] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        carregarDados();
    }, []);

    const carregarDados = async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await axios.get(`/comercial/integracoes/${provider}/formas-pagamento`);
            setMetodos(data.metodos || []);
            setFormasPagamento(data.formasPagamento || []);
        } catch (err) {
            setError(err.response?.data?.message || `Falha ao carregar formas de pagamento da ${label}.`);
        } finally {
            setLoading(false);
        }
    };

    const atualizarStatus = (idExterno, novoVinculado, novoLocalId) => {
        setMetodos(prev =>
            prev.map(m =>
                m.id_externo === idExterno
                    ? { ...m, vinculado: novoVinculado, local_id: novoLocalId }
                    : m
            )
        );
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <span className="spinner-border text-primary" role="status"></span>
                <p className="mt-2 text-muted">Carregando formas de pagamento...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>{error}
            </div>
        );
    }

    const vinculados = metodos.filter(m => m.vinculado).length;

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <p className="text-secondary mb-0">
                    Mapeie cada método de pagamento da {label} para a Forma de Pagamento correspondente no Decision.
                </p>
                <span className="badge bg-secondary">
                    {vinculados}/{metodos.length} vinculados
                </span>
            </div>
            <div className="table-responsive">
                <table className="table table-hover table-bordered align-middle bg-white">
                    <thead className="table-light">
                        <tr>
                            <th scope="col" width="260">Método na Nuvemshop</th>
                            <th scope="col">Vínculo no Sistema (Forma de pagamento)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {metodos.map(m => (
                            <LinhaMetodoPagamento
                                key={m.id_externo}
                                metodo={m}
                                formasPagamento={formasPagamento}
                                provider={provider}
                                onStatusChange={atualizarStatus}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default VincularFormasPagamento;
